import React from 'react';
import { css } from '@emotion/core';
import {
  Container as DNDContainer,
  Draggable as DNDDraggable,
} from 'react-smooth-dnd';
import t from 'react-translate';
import { isEmpty } from 'underscore';

// redux
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'redux/store';
import { getFlatCourseAliases } from 'redux/selectors/course';
import { addQuizQuestionOption, editQuizQuestion } from 'redux/actions/quizzes';
import { ComponentType } from 'redux/schemas/models/lecture-component';

import NvIcon from 'shared/components/nv-icon';
import NvTooltip from 'shared/components/nv-tooltip';
import ClickableContainer from 'components/clickable-container';
import QuizQuestionOption from 'quizzes/components/question-types/multiple-choice-question/quiz-question-option';
import useQuestionSetHelpers from 'quizzes/hooks/use-question-set-helpers';
import { SavingRegistryContext } from 'shared/hooks/use-saving-registry';
import QuestionSetContext, { QuestionContext, SavingIndicator } from 'lecture_pages/services/question-set-context';
import { largeSpacing, threeQuartersSpacing } from 'styles/global_defaults/scaffolding';

type Props = {
  className?: string,
};

const MultipleChoiceAnswerSection = (props: Props) => {
  const { className } = props;

  const dispatch = useAppDispatch();
  const courseAliases = useSelector(getFlatCourseAliases);
  const { registerSaving } = React.useContext(SavingRegistryContext);
  const { responseOptions, currentQuestion } = React.useContext(QuestionContext);
  const {
    lectureComponent,
    canMakeStructuralChanges,
    savingIndicatorTimeoutRef,
    setSavingStatus,
  } = React.useContext(QuestionSetContext);

  const {
    isCurrentActivityType,
    isEditMode,
    isMultipleAnswer,
    dataQaQuestionSetAttributes,
  } = useQuestionSetHelpers();

  const styles = css`
    .smooth-dnd-container.vertical > .smooth-dnd-draggable-wrapper {
      overflow: visible;
    }

    .add-option {
      height: ${largeSpacing}px;
      padding: 0 ${threeQuartersSpacing}px;
    }
  `;

  const renderInstruction = () => {
    if (isCurrentActivityType(ComponentType.SURVEY) && isEditMode) {
      return (
        <div className='label gray-1 mb-2'>
          {isMultipleAnswer
            ? t.SURVEYS.MULTIPLE_SELECTION_CREATOR_INFO({
              learnerAlias: courseAliases.LearnersAlias,
            })
            : t.SURVEYS.SINGLE_SELECTION_CREATOR_INFO({
              learnerAlias: courseAliases.LearnersAlias,
            })}
        </div>
      );
    }
    return (
      <div className='label gray-1 mb-2'>
        {isEditMode ? t.QUIZZES.EDIT_MULTIPLE_OPTION_INSTRUCTION(courseAliases) : t.QUIZZES.MULTIPLE_OPTION_INSTRUCTION(isMultipleAnswer.toString())}
      </div>
    );
  };

  const isOneOptionLeft = currentQuestion.responseOptions.length === 1;

  const mapOptionsToElements = () => responseOptions.map((option, index) => {
    const OptionContainer = isEditMode ? DNDDraggable : 'div';

    return (
      <OptionContainer key={option.id} className='mb-4'>
        <QuizQuestionOption
          index={index}
          option={option}
          isDeleteDisabled={isOneOptionLeft}
        />
      </OptionContainer>
    );
  });

  if (isEditMode) {
    const handleAdd = () => {
      const unregister = registerSaving();
      setSavingStatus(SavingIndicator.SAVING_STATUS);
      clearTimeout(savingIndicatorTimeoutRef.current);

      dispatch(addQuizQuestionOption({ questionId: currentQuestion.id, optionContent: '' })).then((res) => {
        if (isEmpty(res.error)) {
          setSavingStatus(SavingIndicator.SUCCESS_STATUS);
        } else {
          setSavingStatus(SavingIndicator.ERROR_STATUS);
        }
      }).finally(() => {
        unregister();
        savingIndicatorTimeoutRef.current = setTimeout(() => {
          setSavingStatus(SavingIndicator.HIDDEN_STATUS);
        }, 2000);
      });
    };

    const applyDrag = (arr, dragResult) => {
      const { removedIndex, addedIndex, payload } = dragResult;

      if ((removedIndex === null && addedIndex === null) || removedIndex === addedIndex) return;

      const result = [...arr];
      let itemToAdd = payload;
      if (removedIndex !== null) {
        itemToAdd = result.splice(removedIndex, 1)[0];
      }
      if (addedIndex !== null) {
        result.splice(addedIndex, 0, itemToAdd);
      }

      const patch = {
        newOrder: result,
        responseOption: result.reduce((acc, curr, index) => {
          acc[curr] = { index };

          return acc;
        }, {}),
      };

      const unregister = registerSaving();
      setSavingStatus(SavingIndicator.SAVING_STATUS);
      clearTimeout(savingIndicatorTimeoutRef.current);

      dispatch(editQuizQuestion({
        patch,
        id: currentQuestion.id,
      })).then((res) => {
        if (isEmpty(res.error)) {
          setSavingStatus(SavingIndicator.SUCCESS_STATUS);
        } else {
          setSavingStatus(SavingIndicator.ERROR_STATUS);
        }
      }).finally(() => {
        unregister();
        savingIndicatorTimeoutRef.current = setTimeout(() => {
          setSavingStatus(SavingIndicator.HIDDEN_STATUS);
        }, 2000);
      });
    };

    const isAddDisabled = !canMakeStructuralChanges;

    const getReleasedLessonAddOptionDisabledText = () => {
      switch (lectureComponent.type) {
        case ComponentType.QUIZ:
        case ComponentType.PROGRESSIVE_QUIZ:
          return t.QUIZZES.ALREADY_RELEASED.OPTIONS(courseAliases);
        case ComponentType.SURVEY:
          return t.SURVEYS.ALREADY_RELEASED.OPTIONS(courseAliases);
        default:
          return '';
      }
    };

    return (
      <div css={styles} className={`d-flex flex-column ${className}`}>
        {renderInstruction()}
        <DNDContainer
          lockAxis='y'
          dragHandleSelector='.icon-drag-handle'
          onDrop={(e) => applyDrag(currentQuestion.responseOptions, e)}
        >
          {mapOptionsToElements()}
        </DNDContainer>
        <NvTooltip enabled={isAddDisabled} text={getReleasedLessonAddOptionDisabledText()}>
          <ClickableContainer
            onClick={handleAdd}
            disabled={isAddDisabled}
            className={`text-regular align-self-start add-option ${isAddDisabled ? 'gray-4' : ' text-primary'}`}
            data-qa={dataQaQuestionSetAttributes.questionModal.addOption}
            aria-label={t.LECTURE_PAGES.COMPONENTS.POLLS.ADD_OPTION()}
          >
            <NvIcon icon='add' size='small' className='mr-2' />
            {/* Using polls translation to prevent creating new repeated one */}
            {t.LECTURE_PAGES.COMPONENTS.POLLS.ADD_OPTION()}
          </ClickableContainer>
        </NvTooltip>
      </div>
    );
  }

  return (
    <div css={styles} className={className}>
      {renderInstruction()}
      {mapOptionsToElements()}
    </div>
  );
};

export default MultipleChoiceAnswerSection;
